import { Anexo, Endpoint, IeAnexo, MotivoInclusao, onCatchError, Route, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { FetchAPI, FieldAPI, FormAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterNavigate, OnBeforeFetch, OnBlur, OnChange, OnClear, OnMount, OnSelect, OnSubmit, OnValidate } from '@philips-emr/tws-core/lib/client';
import { AbortableFetchEvent, AbortableNavigateEvent, ChangeEvent, ClearEvent, MountEvent, SelectEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { applicationContext } from '@philips-emr/tws-foundation';
import { isEmptyObject, isEmptyOrBlankString } from '@philips-emr/tws-utils';

import { EnderecoData } from '../../../interfaces/logradouro';

import BaseBeneficiarioLoteController, { formAlias, identifiers } from './base-beneficiario-lote-controller';

@Controller({ fragment: 'beneficiario-lote' })
class BeneficiarioLoteController extends BaseBeneficiarioLoteController {

  @OnMount({ target: formAlias.dadosContratacao })
  onMountFormDadosContratacao = (event: MountEvent): void => this.linkFormAndStep(identifiers.dadosContratacao, event.target, formAlias.dadosContratacao);

  @OnMount({ target: formAlias.repasse })
  onMountFormRepasse = (event: MountEvent): void => this.linkFormAndStep(identifiers.repasse, event.target, formAlias.repasse);

  @OnMount({ target: formAlias.dadosBeneficiario })
  onMountFormDadosBeneficiario = (event: MountEvent): void => this.linkFormAndStep(identifiers.dadosBeneficiario, event.target, formAlias.dadosBeneficiario);

  @OnMount({ target: formAlias.portabilidade })
  onMountFormPortabilidade = (event: MountEvent): void => this.linkFormAndStep(identifiers.portabilidade, event.target, formAlias.portabilidade);

  @OnMount({ target: formAlias.sca })
  onMountFormSca = (event: MountEvent): void => this.linkFormAndStep(identifiers.sca, event.target, formAlias.sca);

  @OnMount({ target: formAlias.anexos })
  onMountFormAnexos = (event: MountEvent): void => this.linkFormAndStep(identifiers.anexos, event.target, formAlias.anexos);

  @OnValidate({ target: formAlias.dadosContratacao })
  onValidateFormDadosContratacao = (event: ValidateEvent): void => this.setStepDone(identifiers.dadosContratacao, event.target);

  @OnValidate({ target: formAlias.repasse })
  onValidateFormRepasse = (event: ValidateEvent): void => this.setStepDone(identifiers.repasse, event.target);

  @OnValidate({ target: formAlias.dadosBeneficiario })
  onValidateFormDadosBeneficiario = (event: ValidateEvent): void => this.setStepDone(identifiers.dadosBeneficiario, event.target);

  @OnValidate({ target: formAlias.portabilidade })
  onValidateFormPortabilidade = (event: ValidateEvent): void => this.setStepDone(identifiers.portabilidade, event.target);

  @OnValidate({ target: formAlias.sca })
  onValidateFormSca = (event: ValidateEvent): void => this.setStepDone(identifiers.sca, event.target);

  @OnValidate({ target: formAlias.anexos })
  onValidateFormAnexos = (event: ValidateEvent): void => this.setStepDone(identifiers.anexos, event.target);

  @OnChange({ target: 'field-nrSeqPlano' })
  onChangePlano(e: ChangeEvent<number>): void {
    this.fieldTabelaPreco.setValue(null);
    this.fetchTabelaPreco.setParameters({ ...this.fetchTabelaPreco.getParameters(), nrSeqPlano: e.value ? e.value : null });
    this.fetchTabelaPreco.forceFetch();
  }

  @OnChange({ target: 'field-sgEstado' })
  onChangeSgEstado(e: ChangeEvent<string>): void {
    this.fieldMunicipio.setValue(null);
    this.fetchMunicipio.setParameters({ dsUnidadeFederacao: e.value ? e.value : null });
    this.fetchMunicipio.forceFetch();
  }

  @OnMount({ target: 'wizard-step-anexos' })
  onMountLastStep(): void {
    const { nrSeqLote, nrSeqContrato, nrSeqInclusao } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqLote, nrSeqContrato, nrSeqInclusao });

    if (nrSeqInclusao) {
      this.initData();
      return;
    }

    this.initInclusaoManual();
  }

  @OnChange({ target: 'field-nrSeqMotivoInclusao' })
  onChangeMotivoInclusao(event: ChangeEvent<Record<string, Value>>): void {
    const { value } = event;

    this.validateMotivoInclusao(value as unknown as MotivoInclusao);
  }

  @OnBeforeFetch('fetch-titulares-contrato')
  onBeforeFetch(event: AbortableFetchEvent): void {
    const api = event.target as FetchAPI;
    const nrSeqSubestipulante = StipulatorContext.getCurrentContract().nrSeqSubestipulante;

    nrSeqSubestipulante && api.setParameters({ ...api.getParameters(), nrSeqSubestipulante });
  }

  @OnAfterNavigate({ target: 'wizard-detalhes-benef-lote' })
  onAfterNavigate(event: AbortableNavigateEvent<string>): void {
    const { to, from } = event;

    if (!to) {
      return;
    }

    const step = this.wizardDetalhesBenefLote.getSteps()[to];

    if (!step) {
      return;
    }

    if (this.allowEditing) {
      const fromForm = this.getFormMapByIdentifier(from);
      const toForm = this.getFormMapByIdentifier(to);

      if (!toForm.api.isEditing()) {
        toForm.api.setEditing(true);
      }

      fromForm.api.submit();

      this.setStepDone(to, toForm.api);
    }
  }

  @OnClear({ target: 'wizard-detalhes-benef-lote' })
  onClearStep(event: ClearEvent): void {
    const { entry = '' } = event;

    this.getFormMapByIdentifier(entry).api.setData({});
  }

  @OnChange({ target: Object.values(formAlias) })
  onFormChange(event: ChangeEvent<Record<string, Value>>): void {
    const api = event.target as FormAPI;
    this.recordChanges = { ...this.recordChanges, ...api.state.recordChanges };
  }

  @OnSubmit({ target: 'wizard-detalhes-benef-lote' })
  onSubmitWizard(): void {
    const { nrSeqInclusao, nrSeqLote, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const body = this.getBody();

    const endpoint = nrSeqInclusao ? Endpoint.PUT.BENEFICIARIO_INCLUSAO_LOTE : Endpoint.POST.INCLUIR_BENEF_LOTE;
    const parameters = nrSeqInclusao ? { nrSeqInclusao, nrSeqLote, nrSeqContrato } : { nrSeqLote, nrSeqContrato };
    const method = nrSeqInclusao ? HttpMethod.PUT : HttpMethod.POST;

    HttpService.fetch(endpoint, method, parameters, { body })
      .then(() => applicationContext.redirect(Route.LOTE, { nrSeqLote, nrSeqContrato }))
      .catch(ex => {
        const currentStepIdentifier = this.wizardDetalhesBenefLote.getCurrentStepIdentifier();
        const toForm = this.getFormMapByIdentifier(currentStepIdentifier);

        if (toForm && toForm.api && !toForm.api.isEditing()) {
          toForm.api.setEditing(true);
        }

        if (ex.body) {
          if (typeof ex.body === 'string' && !isEmptyOrBlankString(ex.body)) {
            onCatchError(ex.body);
            return;
          } else if (!isEmptyObject(ex.body)) {
            const value = ex.body[Object.keys(ex.body)[0]];

            if (typeof value !== 'string' || isEmptyOrBlankString(value)) {
              onCatchError();
            } else {
              onCatchError(value);
            }

            return;
          }
        }

        onCatchError();
      });
  }

  @OnSelect({ target: 'lote-attachments-list' })
  onSelectAttachment(event: SelectEvent<Anexo>): void {
    const { value } = event;
    const { nrSeqAnexo, filename } = value;
    const { nrSeqContrato, nrSeqInclusao } = this.historyService.getCurrentRouteParams();
    const ieTabela = IeAnexo.INCLUSAO_ANEXOS;

    FileService.download(Endpoint.GET.INC_BENEF_DOWNLOAD_ANEXO, filename, { ieTabela, nrSeqAnexo, nrSeqContrato, nrSeqInclusao }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));
  }

  @OnBlur({ target: 'field-cdCep' })
  onBlurCdCep(event: FocusEvent): void {
    const api = event.target as unknown as FieldAPI;

    if (api.isValid() && api.getValue()) {
      void HttpService.get<EnderecoData>(Endpoint.GET.ENDERECO_CEP, { cdCep: api.getValue() })
        .then((response) => this.setEndereco(response.body));
    }
  }

  private setEndereco(endereco?: EnderecoData): void {
    if (endereco) {
      const { estado, municipio, dsUf, tipoLogradouro } = endereco;

      const formDadosBeneficiario = this.getFormMapByIdentifier(identifiers.dadosBeneficiario).api;

      const dadosEndereco = {
        ...endereco,
        sgEstado: estado.vlDominio,
        cdMunicipioIbge: municipio.cdMunicipioIbge,
        cdTipoLogradouro: tipoLogradouro.cdTipoLogradouro,
      };

      formDadosBeneficiario.setData({
        ...formDadosBeneficiario.getRecord(),
        ...dadosEndereco,
      });

      this.recordChanges = { ...this.recordChanges, ...dadosEndereco };

      if (dsUf) {
        this.context.set({ dsUnidadeFederacaoSgEstado: dsUf });
        this.fetchMunicipio.forceFetch();
      }
    }
  }
}

export default BeneficiarioLoteController;
